/**
 * All user-facing strings. English-only for MVP (CLAUDE.md §14).
 * Centralised so future i18n is mechanical, not architectural.
 */
export const copy = {
  brand: {
    name: 'wrappa',
    tagline: 'Wrap a digital gift. Share a link. Watch them tear it open.',
    footer: 'Made with care · wrappa.app',
  },
  nav: {
    create: 'Wrap a gift',
    dashboard: 'My gifts',
    signIn: 'Sign in',
    signOut: 'Sign out',
  },
  landing: {
    headline: 'Gifting, with a little drama.',
    subhead:
      'Upload a voucher, choose a wrap, send a link. They have to tear it open before they can use it.',
    primaryCta: 'Wrap your first gift',
    secondaryCta: 'See how it works',
  },
  howItWorks: {
    eyebrow: 'How it works',
    headline: 'A gift they have to open.',
    subhead:
      'Wrappa turns a plain voucher into a moment — a wrapped gift the recipient tears open by hand, with a card waiting inside.',
    senders: {
      title: 'For senders',
      subtitle: 'Three steps, about a minute.',
      steps: [
        {
          title: 'Upload your voucher',
          body: 'Any gift card, code, or PDF — up to 5MB. We never show it until it’s earned.',
        },
        {
          title: 'Wrap it & add a card',
          body: 'Pick a colour, choose how hard it is to tear, and write a personal note.',
        },
        {
          title: 'Share the link',
          body: 'Send it however you like. They unlock it with their first name, then tear in.',
        },
      ],
    },
    demo: {
      title: 'For them, it feels like this',
      subtitle: 'Go on — tear it open. This one’s on us.',
      cta: 'Tap to tear it open',
      ctaReduced: 'Tap to open it',
      hint: 'Swipe across the wrapper to tear it.',
      replay: 'Try it again',
      reducedMotionNote: 'Reduced-motion is on, so we’ll just open it for you.',
      sampleCard: {
        message: 'Hope this brings a little brightness to your day. Enjoy every bit of it!',
        senderName: 'wrappa',
      },
    },
    closing: {
      title: 'Ready to wrap one?',
      body: 'Your first gift takes about a minute.',
      cta: 'Wrap your first gift',
      note: 'Free to send — they don’t need an account.',
    },
  },
  create: {
    title: 'Wrap a gift',
    subtitle: 'Upload the voucher, choose how hard it should be to open, and we\'ll give you a link to share.',
    uploadHint: 'Drop a voucher image or PDF (max 5MB)',
  },
  dashboard: {
    title: 'My gifts',
    empty: {
      title: 'No gifts wrapped yet',
      body: 'When you wrap a gift, it shows up here. You can see whether they\'ve opened it.',
      cta: 'Wrap one now',
    },
    delete: {
      action: 'Delete gift',
      dialogTitle: 'Delete this gift?',
      dialogBody:
        'The link will stop working — if they open it, they\'ll see it was taken back. This can\'t be undone.',
      confirm: 'Delete',
      success: 'Gift deleted.',
      error: 'Could not delete the gift. Try again.',
    },
  },
  reveal: {
    locked: {
      title: "Someone sent you a gift.",
      subtitle: 'Enter your first name to unlock it.',
      placeholder: 'Your first name',
      submit: 'Unlock',
      hint: 'Just your first name — the same one the sender used.',
    },
    unlocking: 'Opening…',
    errors: {
      mismatch: "Hmm, we couldn't open that one. Check the name and try again.",
      lockedOut: 'Too many tries — ask the sender to check the name.',
      notFound: "Hmm, we couldn't open that one. Check the name and try again.",
      expired: 'This gift has expired. Ask the sender to wrap another one.',
      networkError: 'Connection issue. Try again in a moment.',
    },
    removed: {
      title: 'This gift was taken back',
      body: 'The sender removed this gift, so there\'s nothing to unwrap here anymore.',
    },
    escapeHatch: 'Having trouble? Open it for me',
  },
} as const;
